import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Box,
  StatusIndicator,
  Alert,
  Spinner,
  Modal,
  Select,
  Textarea,
  AppLayout,
  FormField,
  Toggle
} from "@cloudscape-design/components";
import { useAuth } from '../components/AuthContext';
import { NavigationSide } from './Navigation';

/**
 * VoiceAssistant component for real-time voice conversation with Polly, Transcribe, and Bedrock
 */
const VoiceAssistant = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [response, setResponse] = useState("");
  const [status, setStatus] = useState({ message: "Disconnected", type: "stopped" });
  const [error, setError] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [sessionTime, setSessionTime] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState({ label: "AB1", value: "AB1" });
  const [customerSituation, setCustomerSituation] = useState("");
  const [prompt, setPrompt] = useState("");
  const [isPromptVisible, setIsPromptVisible] = useState(true);

  const socketRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const timerRef = useRef(null);
  const conversationContainerRef = useRef(null);

  const { isAuthenticated } = useAuth();

  // Role-specific styling
  const getRoleStyle = (role) => {
    const roleStyles = {
      CEO: { 
        backgroundColor: '#1f2937', // Dark gray
        color: 'white',
        initial: 'C'
      },
      CTO: { 
        backgroundColor: '#059669', // Green
        color: 'white',
        initial: 'T'
      },
      CFO: { 
        backgroundColor: '#dc2626', // Red
        color: 'white',
        initial: 'F'
      },
      CISO: { 
        backgroundColor: '#7c3aed', // Purple
        color: 'white',
        initial: 'S'
      },
      user: {
        backgroundColor: '#2563eb', // Blue (existing)
        color: 'white',
        initial: 'U'
      }
    };
    
    return roleStyles[role] || roleStyles.CEO;
  };

  // Initialize audio context
  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();

    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      disconnect();
    };
  }, []);

  // Scroll conversation to bottom when new messages arrive
  useEffect(() => {
    if (conversationContainerRef.current) {
      conversationContainerRef.current.scrollTop = conversationContainerRef.current.scrollHeight;
    }
  }, [conversation]);

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" + secs : secs}`;
  };

  // Start session timer
  const startSessionTimer = () => {
    setSessionTime(0);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      setSessionTime((prevTime) => prevTime + 1);
    }, 1000);
  };

  // Connect to WebSocket server
  const connect = async () => {
    try {
      setError(null);
      setStatus({ message: "Connecting...", type: "in-progress" });
      setIsProcessing(false); // Reset processing state on connect

      // Get authentication token
      const token = localStorage.getItem("idToken");
      console.log(`id token: ${token}`)
      if (!token) {
        throw new Error("Authentication token not available");
      }

      // Connect to WebSocket with token
      // const wsUrl = `ws://localhost:80/${token}`;  // Changed from 0.0.0.0 to localhost and added slash
      // const wsUrl = `ws://eloquence-sonic-3a9990e00289a710.elb.us-east-1.amazonaws.com/${token}`;  // Changed from 0.0.0.0 to localhost and added slash
      // const wsUrl = `wss://dfpegpq4ou0d8.cloudfront.net/voice/${token}`;  // Changed from 0.0.0.0 to localhost and added slash
			const wsUrl = `https://d1u3pka9oxzjz9.cloudfront.net/voice/${token}`
      console.log("Connecting to WebSocket URL:", wsUrl);
      socketRef.current = new WebSocket(wsUrl);

      socketRef.current.onopen = () => {
        console.log("WebSocket connected");
        setIsConnected(true);
        setIsProcessing(false); // Ensure processing is reset when connection opens
        setStatus({ message: "Connected", type: "success" });

        // Generate conversation ID and send START event
        const newConversationId = `call-${Date.now()}`;
        setConversationId(newConversationId);
        socketRef.current.send(JSON.stringify({
          callEvent: "START",
          callId: newConversationId,
          agentId: "user@example.com", // Replace with actual user ID or email
          samplingRate: 16000, // Match Transcribe configuration
          category: selectedCategory.value,
          customerSituation: prompt
        }));

        // Start session timer
        startSessionTimer();
      };

      socketRef.current.onmessage = (event) => {
        console.log("Received WebSocket message:", event.data);
        try {
          const data = JSON.parse(event.data);
          console.log("Parsed message:", data);

          switch (data.type) {
            case "transcript":
              // Display transcript to user
              console.log("Received transcript:", data.transcript);
              setTranscript(data.transcript);
              setConversation(prev => [...prev, { role: "user", message: data.transcript }]);
              break;

            case "response":
              // Display text response to user (skip if response is "None")
              console.log("Received response:", data.text);
              if (data.text && data.text.trim() !== "None") {
                setResponse(data.text);
                setConversation(prev => [...prev, { 
                  role: "assistant", 
                  message: data.text,
                  executiveRole: data.role // Store which executive is speaking
                }]);
              } else {
                console.log("Skipping display of 'None' response");
              }
              setIsProcessing(false);
              break;

            case "audio":
              // Play audio response
              console.log("Received audio, length:", data.audio.length);
              playAudio(data.audio);
              break;

            default:
              console.log("Received unknown message type:", data);
          }
        } catch (error) {
          console.error("Error processing WebSocket message:", error);
        }
      };

      socketRef.current.onclose = () => {
        console.log("WebSocket disconnected");
        setIsConnected(false);
        setIsProcessing(false); // Reset processing on connection close
        setStatus({ message: "Disconnected", type: "stopped" });

        // Clear timer
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      };

      socketRef.current.onerror = (error) => {
        console.error("WebSocket error:", error);
        setError("Connection error: " + error.message);
        setStatus({ message: "Error", type: "error" });
      };
    } catch (error) {
      console.error("Error connecting to WebSocket:", error);
      setError("Failed to connect: " + error.message);
      setStatus({ message: "Connection failed", type: "error" });
    }
  };

  // Disconnect from WebSocket server
  const disconnect = () => {
    if (socketRef.current) {
      // Send END event
      if (socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({
          callEvent: "END"
        }));
      }

      socketRef.current.close();
      socketRef.current = null;
    }

    if (isRecording) {
      stopRecording();
    }

    setIsConnected(false);
    setStatus({ message: "Disconnected", type: "stopped" });
    setIsMuted(false); // Reset mute state when disconnecting
    setIsProcessing(false); // Reset processing state on disconnect

    // Clear timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Show upload modal if there's conversation history
    if (conversation.length > 0) {
      setShowUploadModal(true);
    }
  };

  const uploadConversation = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const transcript = conversation.map(msg => `${msg.role.toUpperCase()}: ${msg.message}`).join('\n');
      var date = new Date();
      const fileName = `Voice AI Assistant ${date.toDateString()}.txt`;

      // Get presigned URL from backend
      // const urlResponse = await fetch('https://dfpegpq4ou0d8.cloudfront.net/api/upload-url', {
      const urlResponse = await fetch('https://d1u3pka9oxzjz9.cloudfront.net/api/upload-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-MyApp-Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ fileName })
      });

      const { uploadUrl } = await urlResponse.json();

      // Upload transcript to S3
      await fetch(uploadUrl, {
        method: 'PUT',
        body: transcript,
        headers: { 'Content-Type': 'text/plain' }
      });

      alert('Conversation uploaded for grading!');
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload conversation');
    }
    setShowUploadModal(false);
  };

  // Start recording audio
  const startRecording = async () => {
    if (!isConnected) {
      setError("WebSocket not connected");
      return;
    }

    try {
      console.log("Requesting microphone access...");
      // Request audio with echo cancellation enabled
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,   // ← Turn back ON for feedback prevention
          noiseSuppression: true,   // ← Turn back ON for better quality
          autoGainControl: true     // ← Turn back ON for consistent levels
        }
      });
      console.log("Microphone access granted, stream:", stream);

      // Create audio context with matching sample rate
      if (!audioContextRef.current || audioContextRef.current.sampleRate !== 16000) {
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
          sampleRate: 16000
        });
      }

      // Resume audio context if suspended (required by some browsers)
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
        console.log("Audio context resumed");
      }

      console.log("Audio context state:", audioContextRef.current.state);
      console.log("Audio context sample rate:", audioContextRef.current.sampleRate);

      // Create a ScriptProcessorNode for direct PCM access (fallback method)
      // Note: ScriptProcessorNode is deprecated but still widely supported
      const bufferSize = 4096; // Larger buffer for better performance
      const processor = audioContextRef.current.createScriptProcessor(bufferSize, 1, 1);
      const source = audioContextRef.current.createMediaStreamSource(stream);

      console.log("Created audio nodes - buffer size:", bufferSize);

      // Connect the audio nodes
      source.connect(processor);
      processor.connect(audioContextRef.current.destination);

      // Process audio data in real-time
      processor.onaudioprocess = (e) => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN && !isMuted) {
          // Get raw PCM data from input buffer (force mono by using only channel 0)
          const inputData = e.inputBuffer.getChannelData(0);

          // Log channel info for debugging
          const channelCount = e.inputBuffer.numberOfChannels;
          if (channelCount > 1) {
            console.log(`Warning: Received ${channelCount} channels, using only channel 0`);
          }

          // Convert to 16-bit PCM and send continuously
          const pcm16 = new Int16Array(inputData.length);
          for (let i = 0; i < inputData.length; i++) {
            pcm16[i] = Math.max(-32768, Math.min(32767, Math.round(inputData[i] * 32767)));
          }

          // Debug logging
          const audioLevel = Math.max(...inputData.map(Math.abs));
          if (audioLevel > 0.01) { // Only log when there's actual audio
            console.log(`Sending audio chunk: ${pcm16.length} samples, ${channelCount} channels, level: ${audioLevel.toFixed(4)}`);
          }

          // Send all audio data continuously for transcription
          socketRef.current.send(pcm16.buffer);
        }
      };

      // Store references for cleanup
      mediaRecorderRef.current = {
        stream,
        source,
        processor,
        stop: () => {
          source.disconnect();
          processor.disconnect();
          stream.getTracks().forEach(track => track.stop());
        }
      };

      setIsRecording(true);
      setIsMuted(false); // Start unmuted
      setStatus({ message: "Recording", type: "in-progress" });
    } catch (error) {
      console.error("Error starting recording:", error);
      setError("Failed to start recording: " + error.message);
    }
  };

  // Stop recording audio
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      // Use the stop method we defined in the mediaRecorderRef
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;

      setIsRecording(false);
      setIsMuted(false); // Reset mute state when stopping recording
      setIsProcessing(true);
      setStatus({ message: "Processing", type: "in-progress" });
    }
  };

  // Play audio from base64 string
  const playAudio = async (base64Audio) => {
    // Add to queue
    audioQueueRef.current.push(base64Audio);

    // If not already playing, start playing
    if (!isPlayingRef.current) {
      playNextInQueue();
    }
  };

  // Play next audio in queue
  const playNextInQueue = async () => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      return;
    }

    isPlayingRef.current = true;
    const base64Audio = audioQueueRef.current.shift();

    try {
      // Convert base64 to ArrayBuffer
      const binaryString = window.atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Decode audio
      const audioBuffer = await audioContextRef.current.decodeAudioData(bytes.buffer);

      // Create source and play
      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContextRef.current.destination);

      // When finished, play next in queue
      source.onended = () => {
        playNextInQueue();
      };

      source.start();
    } catch (error) {
      console.error("Error playing audio:", error);
      // Continue to next audio in queue even if there's an error
      playNextInQueue();
    }
  };

  // Toggle recording
  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // Toggle mute
  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  return (
    <AppLayout
      toolsHide={true}
      navigation={<NavigationSide activeHref="/voice-assistant" />}
      content={
        <Container>
          <SpaceBetween size="l">
            <Header variant="h1" description="Present to a live panel CEO, CFO, CTO, and CISO!">
              Live Presentation
            </Header>

            {error && (
              <Alert type="error" dismissible onDismiss={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Configuration Section */}
            <Box padding="l">
              <SpaceBetween size="m">
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "16px" }}>
                  <FormField label="AB Test Category">
                    <Select
                      selectedOption={selectedCategory}
                      onChange={({ detail }) => setSelectedCategory(detail.selectedOption)}
                      options={[
                        { label: "AB1", value: "AB1" },
                        { label: "AB2", value: "AB2" },
                        { label: "AB3", value: "AB3" }
                      ]}
                      disabled={isConnected}
                    />
                  </FormField>
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <label style={{ fontWeight: '600', fontSize: '14px', color: '#ffffff' }}>
                      Customer Situation
                    </label>
                    <Toggle
                      onChange={({ detail }) => setIsPromptVisible(detail.checked)}
                      checked={isPromptVisible}
                      disabled={isConnected}
                      ariaLabel="Show/hide customer situation section"
                    >
                      {isPromptVisible ? "Hide" : "Show"}
                    </Toggle>
                  </div>
                  {isPromptVisible && (
                    <Textarea
                      value={prompt}
                      onChange={({ detail }) => setPrompt(detail.value)}
                      placeholder="Describe the customer's situation, challenges, or context (e.g., Large enterprise looking to migrate to cloud, concerned about security and compliance...)"
                      rows={4}
                      disabled={isConnected}
                    />
                  )}
                </div>
              </SpaceBetween>
            </Box>

            <Box padding="l">
              <SpaceBetween size="m">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <div>🕐 {formatTime(sessionTime)}</div>
                    <StatusIndicator
                      type={
                        status.type === "success"
                          ? "success"
                          : status.type === "in-progress"
                            ? "in-progress"
                            : status.type === "error"
                              ? "error"
                              : "stopped"
                      }
                    >
                      {status.message}
                    </StatusIndicator>
                  </div>

                  <SpaceBetween direction="horizontal" size="xs">
                    {!isConnected ? (
                      <Button
                        variant="primary"
                        onClick={connect}
                        disabled={!isAuthenticated}
                      >
                        Connect
                      </Button>
                    ) : (
                      <Button
                        variant="normal"
                        onClick={disconnect}
                      >
                        Disconnect
                      </Button>
                    )}
                  </SpaceBetween>
                </div>

                {/* Conversation container */}
                <div
                  ref={conversationContainerRef}
                  style={{
                    border: '1px solid #414d5c',
                    borderRadius: '8px',
                    padding: '24px',
                    height: '400px',
                    overflowY: 'auto',
                    backgroundColor: '#0f1419',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    position: 'relative'
                  }}
                >
                  {conversation.length === 0 ? (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: '#879196',
                      fontSize: '16px',
                      textAlign: 'center'
                    }}>
                      <div style={{
                        fontSize: '48px',
                        marginBottom: '16px',
                        opacity: 0.6
                      }}>
                        🎤
                      </div>
                      <div style={{ fontWeight: '500', marginBottom: '8px' }}>
                        Ready to start conversation
                      </div>
                      <div style={{ fontSize: '14px', opacity: 0.8 }}>
                        {isConnected
                          ? "Click the microphone button to speak"
                          : "Click \"Connect\" to begin"}
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {conversation.map((item, index) => {
                        const displayRole = item.role === "user" ? "user" : (item.executiveRole || "CEO");
                        const roleStyle = getRoleStyle(displayRole);
                        
                        return (
                          <div
                            key={index}
                            style={{
                              display: 'flex',
                              flexDirection: item.role === 'user' ? 'row-reverse' : 'row',
                              alignItems: 'flex-start',
                              gap: '12px',
                              maxWidth: '85%',
                              alignSelf: item.role === 'user' ? 'flex-end' : 'flex-start'
                            }}
                          >
                            <div style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              backgroundColor: roleStyle.backgroundColor,
                              border: item.role === 'user' ? 'none' : '2px solid #414d5c',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              color: roleStyle.color,
                              flexShrink: 0
                            }}>
                              {roleStyle.initial}
                            </div>
                            <div
                              style={{
                                padding: '12px 16px',
                                borderRadius: '12px',
                                backgroundColor: item.role === 'user' ? '#0073bb' : roleStyle.backgroundColor,
                                border: item.role === 'user' ? 'none' : '1px solid #414d5c',
                                color: roleStyle.color,
                                fontSize: '14px',
                                lineHeight: '1.5',
                                wordWrap: 'break-word',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                                position: 'relative'
                              }}
                            >
                              <div style={{
                                fontSize: '12px',
                                fontWeight: '600',
                                marginBottom: '4px',
                                opacity: 0.8,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                {item.role === 'user' ? 'You' : (item.executiveRole || 'CEO')}
                              </div>
                              <div>{item.message}</div>
                              {/* Chat bubble tail */}
                              <div style={{
                                position: 'absolute',
                                top: '16px',
                                [item.role === 'user' ? 'right' : 'left']: '-6px',
                              width: '12px',
                              height: '12px',
                              backgroundColor: item.role === 'user' ? '#0073bb' : roleStyle.backgroundColor,
                              border: item.role === 'user' ? 'none' : '1px solid #414d5c',
                              borderTop: 'none',
                              borderLeft: item.role === 'user' ? 'none' : '1px solid #414d5c',
                              transform: item.role === 'user' ? 'rotate(45deg)' : 'rotate(-45deg)',
                              zIndex: -1
                            }} />
                            </div>
                          </div>
                        );
                      })}

                      {isProcessing && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', alignSelf: 'flex-start' }}>
                          <Spinner size="normal" />
                          <span style={{ color: '#879196' }}>Processing...</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Microphone button */}
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                  <Button
                    variant={isRecording && !isMuted ? "primary" : "normal"}
                    iconName={isRecording ? "microphone" : "microphone-off"}
                    onClick={isRecording ? toggleMute : toggleRecording}
                    disabled={!isConnected || isProcessing}
                    style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  />
                </div>

                <Modal
                  onDismiss={() => setShowUploadModal(false)}
                  visible={showUploadModal}
                  header="Upload Conversation"
                  footer={
                    <Box float="right">
                      <SpaceBetween direction="horizontal" size="xs">
                        <Button onClick={() => setShowUploadModal(false)}>Cancel</Button>
                        <Button variant="primary" onClick={uploadConversation}>
                          Upload for Grading
                        </Button>
                      </SpaceBetween>
                    </Box>
                  }
                >
                  <Box>
                    <p>Would you like to upload this conversation for grading analysis?</p>
                    <p>This will send the transcript to the grading system for evaluation and feedback.</p>
                  </Box>
                </Modal>

              </SpaceBetween>
            </Box>
          </SpaceBetween>
        </Container>
      }>
    </AppLayout>
  );
};

export default VoiceAssistant;
